import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatErr } from "@/lib/api";
import { useLang } from "@/i18n";
import { Sparkles, RefreshCcw, ArrowRight } from "lucide-react";

const fmtBytes = (b) => {
  const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let n = b || 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
};

export default function CapsuleBuilder() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [fileIds, setFileIds] = useState([]);
  const [hint, setHint] = useState("");
  const [capsule, setCapsule] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const raw = sessionStorage.getItem("s2s_capsule_files");
    if (!raw) { nav("/upload"); return; }
    const ids = JSON.parse(raw);
    setFileIds(ids);
    generate(ids, "");
  }, []); // eslint-disable-line

  const generate = async (ids, hintText) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/capsules", { file_ids: ids, hint: hintText, language: lang });
      setCapsule(data);
    } catch (ex) { setErr(formatErr(ex)); } finally { setBusy(false); }
  };

  const saveAndContinue = async () => {
    if (!capsule) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch(`/capsules/${capsule.id}`, {
        capsule_name: capsule.capsule_name, summary: capsule.summary,
      });
      // Route to galactic navigation to choose destination
      sessionStorage.removeItem("s2s_capsule_files");
      nav(`/galaxy?capsule=${data.id}`);
    } catch (ex) { setErr(formatErr(ex)); } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <div className="label-overline">Stage 2 / Mission</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="capsule-title">{t("capsule.title")}</h1>
        <p className="text-white/55 mt-2 flex items-center gap-2">
          <Sparkles size={14} className="text-[#00D4FF]" /> {t("capsule.subtitle")}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="holo-panel p-6" data-testid="capsule-form">
          <label className="label-overline">{t("capsule.hint")}</label>
          <textarea
            value={hint} onChange={(e) => setHint(e.target.value)}
            rows={3}
            data-testid="capsule-hint-input"
            className="w-full mt-2 bg-black/40 border border-[#00D4FF]/15 focus:border-[#00D4FF] outline-none p-3 text-white text-sm"
            placeholder={lang === "pt" ? "Ex.: Mensagens para meus netos…" : "E.g. Messages for my grandchildren…"}
          />

          <div className="mt-6">
            <label className="label-overline">{t("capsule.name")}</label>
            <input
              data-testid="capsule-name-input"
              value={capsule?.capsule_name || ""}
              onChange={(e) => setCapsule({ ...capsule, capsule_name: e.target.value })}
              className="w-full mt-2 bg-transparent border-b border-[#00D4FF]/25 focus:border-[#00D4FF] outline-none py-2 text-white font-display text-xl tracking-tight"
            />
          </div>

          <div className="mt-6">
            <label className="label-overline">{t("capsule.summary")}</label>
            <textarea
              rows={5}
              data-testid="capsule-summary-input"
              value={capsule?.summary || ""}
              onChange={(e) => setCapsule({ ...capsule, summary: e.target.value })}
              className="w-full mt-2 bg-black/40 border border-[#00D4FF]/15 focus:border-[#00D4FF] outline-none p-3 text-white text-sm leading-relaxed"
            />
          </div>

          {err && <div className="mt-3 text-[#FF3366] font-mono-data text-xs" data-testid="capsule-error">{err}</div>}

          <div className="mt-6 flex gap-3">
            <button
              onClick={() => generate(fileIds, hint)}
              disabled={busy}
              data-testid="capsule-regenerate"
              className="btn-secondary-s2s !py-3 !text-[11px] disabled:opacity-50"
            >
              <RefreshCcw size={12} /> {t("capsule.regenerate")}
            </button>
            <button
              onClick={saveAndContinue}
              disabled={busy || !capsule}
              data-testid="capsule-transmit"
              className="btn-primary-s2s !py-3 !text-[11px] disabled:opacity-50"
            >
              {lang === "pt" ? "ESCOLHER DESTINO →" : "CHOOSE DESTINATION →"} <ArrowRight size={12} />
            </button>
          </div>
        </div>

        <div className="holo-panel p-6 relative overflow-hidden" data-testid="capsule-preview">
          <div className="label-overline">Capsule Preview</div>
          <div className="mt-6">
            <div className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/55">{t("capsule.file_count")}</div>
            <div className="font-display font-black text-3xl mt-1 tracking-tighter">{fileIds.length}</div>
          </div>
          <div className="mt-6">
            <div className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/55">{t("capsule.size")}</div>
            <div className="font-display font-black text-3xl mt-1 tracking-tighter">{fmtBytes(capsule?.total_size || 0)}</div>
          </div>
          <div className="mt-6">
            <div className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/55">{t("capsule.date")}</div>
            <div className="font-mono-data text-sm mt-1 text-[#00D4FF]">{new Date().toUTCString()}</div>
          </div>
          {busy && (
            <div className="mt-8 font-mono-data text-[11px] tracking-[0.2em] uppercase text-[#00D4FF] blink">
              {t("capsule.generating")}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
